import { useState, type ReactElement } from 'react';
import { useI18n } from '../lib/i18n/I18nProvider';
import { API_BASE } from '../lib/api/client';

/**
 * THE REVIEWER SEES WHAT THEY ARE APPROVING.
 *
 * Branding bytes sit in quarantine until the scanner clears them, so there is no public URL to
 * point an `<img>` at while a reviewer is deciding. The two preview routes serve those bytes to a
 * principal who is allowed to look — `branding.review` for the admin desk, the owning organization
 * for the exhibitor — and both are same-origin under `/api`, so the session cookie authorises the
 * image request exactly as it authorises every other read on this surface.
 *
 * A preview that fails to load says so and offers to try again. It never silently renders an empty
 * frame: "no image appeared" and "this asset is not an image the server will show you" are
 * different answers, and only one of them is a reason to approve nothing.
 */

export function adminMediaPreviewUrl(mediaAssetId: string): string {
  return `${API_BASE}/v1/admin/media/${mediaAssetId}/preview`;
}

export function developerMediaPreviewUrl(participationId: string, mediaAssetId: string): string {
  return `${API_BASE}/v1/developer/booths/${participationId}/media/${mediaAssetId}/preview`;
}

export function MediaPreview({ src, alt }: { src: string; alt: string }): ReactElement {
  const { t } = useI18n();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc === src) {
    return (
      <div className="media-preview media-preview--failed" role="status">
        <span>{t('media.preview.unavailable')}</span>
        <button type="button" className="btn btn--secondary btn--small" onClick={() => setFailedSrc(null)}>
          {t('state.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="media-preview">
      <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailedSrc(src)} />
    </div>
  );
}
